import { Navigate, useLocation } from "@tanstack/react-router";
import { useAuth } from "@clerk/clerk-react";
import { useSelector } from "react-redux";
import SessionLoading from "./SessionLoading";
import Unauthorized from "./unauthorized";

// Usage:
// <ProtectedRoute requiredRoles={["Admin"]}>
//   <AdminPortal />
// </ProtectedRoute>
// If requiredRoles is empty or undefined, route is considered public (no restriction).
export default function ProtectedRoute({ requiredRoles = [], children }) {
  const location = useLocation();
  const { isLoaded, isSignedIn } = useAuth();
  const { user, roles, rolesStatus } = useSelector((s) => s.auth);

  // Allow sign-in and sign-up for everyone
  if (["/sign-in", "/sign-up"].some((p) => location.pathname.startsWith(p))) {
    return children;
  }

  // Wait for Clerk to hydrate
  if (!isLoaded) {
    return <SessionLoading />;
  }

  // If not logged in, block everything except auth routes.
  // Requirement: unauthenticated users should be redirected to sign-in.
  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  // Roles are required for all signed-in users in this app.
  // Wait for roles if we're still loading (or not yet bootstrapped).
  if (rolesStatus === "loading" || rolesStatus === "idle") {
    return <SessionLoading />;
  }

  // If user has no roles or only has the Student role, block.
  if (
    (!roles ||
      roles.length === 0 ||
      (roles.length === 1 && roles.includes("Student"))) &&
    (rolesStatus === "succeeded" || rolesStatus === "failed")
  ) {
    return <Unauthorized />;
  }

  // If route is public (no requiredRoles), allow (we already ensured signed-in + roles above).
  if (!requiredRoles || requiredRoles.length === 0) return children;

  // Extra safety: if redux user is missing for some reason, send user to auth flow.
  if (!user) return <Navigate to="/sign-in" replace />;

  const hasAdmin = roles?.includes("Admin");
  const hasRequired = hasAdmin || requiredRoles.some((r) => roles?.includes(r));

  if (!hasRequired) {
    // Unauthorized: redirect to home or a dedicated page
    //console.log("User is not authorized");
    return <Navigate to="/" replace />;
  }

  return children;
}
